import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MailIcon from "@mui/icons-material/MailOutlineRounded";
import {
  Alert,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDebounce from "@entrilia/shared/hooks/useDebounce";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import {
  combineValidators,
  emailValidator,
  maxCharactersValidator,
  requiredValidator,
} from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../../api/adminApi";
import type {
  Contact,
  ContactEmailChangePlan,
  ContactEmailOwnership,
} from "../../../../../api/types/fundStructureTypes";
import { checkContactEmailAvailability, emailAvailabilityUnknownText } from "./contactEmailAvailability";
import type { EmailChangePersonAnswer } from "./contactEmailChangeDialogModel";
import {
  getEmailChangeErrorMessage,
  getEmailChangePersonAnswerExplanation,
  getEmailChangeSuccessMessage,
  getInvitationDisabledReason,
  isAddressNowOwnedError,
  isInvitationRelevant,
  isValidEmailChangePersonAnswer,
  keepInvitationChecked,
  resolveDialogState,
} from "./contactEmailChangeDialogModel";
import EmailOwnershipNotice from "./EmailOwnershipNotice";

interface Props {
  contact: Contact;
  plan: ContactEmailChangePlan;
  onClose: () => void;
  onSaved: () => void;
}

const changeContactEmailAndFundPortalLogin = withErrorHandling(adminApi.changeContactEmailAndFundPortalLogin);

const validateEmail = combineValidators(requiredValidator, emailValidator);
const validateName = combineValidators(requiredValidator, maxCharactersValidator(100));

const maxNameLength = 100;

const ContactEmailChangeDialog = ({ contact, plan, onClose, onSaved }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [email, setEmail] = useState(contact.email);
  const [name, setName] = useState(contact.name);
  const [shouldSendInvitation, setShouldSendInvitation] = useState(false);
  const [answer, setAnswer] = useState<EmailChangePersonAnswer>();
  const [ownership, setOwnership] = useState<ContactEmailOwnership>();
  const [historyTransferConfirmed, setHistoryTransferConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const latestEmailRef = useRef(email);

  const checkAvailabilityDebounced = useDebounce(async (typedEmail: string) => {
    const resolvedOwnership = await checkContactEmailAvailability(contact.id, typedEmail, "[ContactEmailChangeDialog]");
    if (latestEmailRef.current !== typedEmail) {
      return;
    }

    setOwnership(resolvedOwnership);
  }, 300);

  const dialogState = resolveDialogState({ answer, ownership, historyTransferConfirmed });
  const invitationRelevant = isInvitationRelevant({ answer, ownership, plan });
  const willSendInvitation = shouldSendInvitation && invitationRelevant;

  const submitEmailChange = async (payload: { isSamePerson: boolean; historyTransferConfirmed: boolean }) => {
    setLoading(true);

    const submittedEmail = email;
    const sanitizedEmail = sanitizeEmail(email);
    const newName = name.trim() !== contact.name.trim() ? name : undefined;
    const [resp, error] = await changeContactEmailAndFundPortalLogin(contact.id, {
      newEmail: sanitizedEmail,
      newName,
      sendInvitation: willSendInvitation,
      isSamePerson: payload.isSamePerson,
      historyTransferConfirmed: payload.historyTransferConfirmed,
    });

    setLoading(false);

    if (error) {
      logError(error, "[ContactEmailChangeDialog] changeContactEmailAndFundPortalLogin");
      // Record the claim so the notice appears, instead of a state that can only be refused again
      const submissionIsCurrent = latestEmailRef.current === submittedEmail;
      if (isAddressNowOwnedError(error) && submissionIsCurrent) {
        setOwnership("Taken");
        setHistoryTransferConfirmed(false);
      }
      sendNotificationError(getEmailChangeErrorMessage(error, submissionIsCurrent));
      return;
    }

    sendNotification(
      getEmailChangeSuccessMessage(resp.outcome, resp.name, {
        requested: willSendInvitation,
        sent: resp.invitationSent,
      })
    );
    onClose();
    onSaved();
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    latestEmailRef.current = value;
    setHistoryTransferConfirmed(false);
    setOwnership(undefined);

    setShouldSendInvitation(
      keepInvitationChecked({ checked: shouldSendInvitation, answer, ownership: undefined, plan })
    );

    const sanitized = sanitizeEmail(value);
    if (validateEmail(sanitized).isValid && sanitized.toLowerCase() !== contact.email.trim().toLowerCase()) {
      checkAvailabilityDebounced(value);
    }
  };

  const handleAnswerChange = (value: string) => {
    if (!isValidEmailChangePersonAnswer(value)) {
      return;
    }

    setAnswer(value);

    setShouldSendInvitation(keepInvitationChecked({ checked: shouldSendInvitation, answer: value, ownership, plan }));
  };

  const handleSave = () => {
    if (dialogState.payload) {
      submitEmailChange(dialogState.payload);
    }
  };

  const emailValidationResult = validateEmail(sanitizeEmail(email));
  const nameValidationResult = validateName(name);
  const hasEmailChanged = sanitizeEmail(email).toLowerCase() !== contact.email.trim().toLowerCase();

  const isSaveDisabled =
    !hasEmailChanged || !emailValidationResult.isValid || !nameValidationResult.isValid || !dialogState.saveEnabled;

  const isSendInvitationDisabled = !contact.hasAccessToInvestorPortal || !invitationRelevant;
  const invitationDisabledReason = getInvitationDisabledReason({
    hasPortalAccess: contact.hasAccessToInvestorPortal,
    isInvitationRelevant: invitationRelevant,
  });

  const personAnswerExplanation = dialogState.noticeShown
    ? undefined
    : getEmailChangePersonAnswerExplanation(answer, plan);

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Change sign-in email</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />

      <DialogContent>
        <Stack spacing={2}>
          <Stack spacing={1} sx={{ pb: 1 }}>
            <Typography variant="subtitle2">
              Is this the same person with a new email address, or someone new taking over this contact?
            </Typography>

            <RadioGroup value={answer ?? ""} onChange={(e) => handleAnswerChange(e.target.value)}>
              <FormControlLabel value="same_person" control={<Radio />} label="Same person, new email address" />
              <FormControlLabel
                value="someone_new"
                control={<Radio />}
                label="Someone new is taking over this contact"
              />
            </RadioGroup>

            {personAnswerExplanation && (
              <Typography variant="caption" color="textSecondary">
                {personAnswerExplanation}
              </Typography>
            )}
          </Stack>

          <TextField
            fullWidth
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            label="Email"
            error={!emailValidationResult.isValid}
            helperText={emailValidationResult.error}
            slotProps={{
              input: { startAdornment: <MailIcon color="action" sx={{ mr: 1 }} /> },
            }}
          />

          {dialogState.lookupFailed && <Alert severity="error">{emailAvailabilityUnknownText}</Alert>}

          {dialogState.noticeShown && (
            <EmailOwnershipNotice
              text="This address already has an Entrilia account. Transferring this contact hands it to that person: they sign in with their existing account and receive this contact’s documents, messages and portal activity."
              confirmLabel="I understand, and want to transfer this contact."
              confirmed={historyTransferConfirmed}
              onConfirmedChange={setHistoryTransferConfirmed}
            />
          )}

          <TextField
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            label="Name"
            error={!nameValidationResult.isValid}
            helperText={nameValidationResult.error}
            slotProps={{
              htmlInput: { maxLength: maxNameLength },
              input: {
                endAdornment: (
                  <Typography variant="caption" color="textSecondary">
                    {maxNameLength - name.length}
                  </Typography>
                ),
              },
            }}
          />

          {answer && (
            <Stack direction="row" spacing={0.5} sx={{ alignSelf: "flex-start", alignItems: "center" }}>
              <FormControlLabel
                label="Send invitation email"
                control={
                  <Checkbox checked={willSendInvitation} onChange={(_, checked) => setShouldSendInvitation(checked)} />
                }
                disabled={isSendInvitationDisabled}
                sx={{ mr: 0 }}
              />
              {invitationDisabledReason && (
                <Tooltip arrow describeChild title={invitationDisabledReason}>
                  <InfoOutlinedIcon color="action" />
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={loading} onClick={handleSave} disabled={isSaveDisabled}>
          {willSendInvitation ? "Save & Invite" : "Save"}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ContactEmailChangeDialog;
